/** @format */

import {
	AppProductReviewState,
	ProductReviewed,
	Review,
	ReviewReturn,
	ReviewService,
	ReviewSummary,
	Statistics,
	addProductReviewsMultimedia,
	selectProductIncludedInformation,
	selectProductReviewSummary,
	setBVPixelIsAvailable,
	setProductReviewState,
	setProductStatistics
} from '@product-reviews/data-access';
import {
	BVEventObject,
	BVEventType,
	BVInViewData,
	BVProduct,
	MediaSourceEnum,
	TrackViewUCGObject
} from '../models';
import {
	Component,
	ElementRef,
	Input,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	Renderer2,
	inject
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
	EventBusService,
	Events,
	IntersectionObservingService,
	KillswitchService,
	UtilityService
} from '@shared/data-access/services';
import { Observable, Subject, Subscription, forkJoin } from 'rxjs';
import { map, take, takeUntil, tap } from 'rxjs/operators';

import { KillSwitches } from '@shared/data-access/interfaces';
import { Store } from '@ngrx/store';
import { chain } from '@core/chain';
import { selectKillswitch } from '@pdp/data-access/state';

@Component({
	selector: 'product-reviews-highlight-container',
	templateUrl: './review-highlight-container.component.html',
	styleUrl: './review-highlight-container.component.scss',
	standalone: false
})
export class ReviewHighlightContainerComponent implements OnInit, OnDestroy {
	private readonly store = inject<Store<AppProductReviewState>>(Store);
	private readonly utilityService = inject(UtilityService);
	private readonly killSwitchService = inject(KillswitchService);
	private readonly reviewService = inject(ReviewService);
	private readonly eventBusService = inject(EventBusService);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly document = inject(DOCUMENT);
	private readonly intersectionService = inject(IntersectionObservingService);
	private readonly renderer = inject(Renderer2);
	private readonly el = inject(ElementRef);

	/**
	 *
	 * @description
	 * Product Id - Bazaar voice uses this identifier to display the correct reviews
	 */
	@Input() productIdentifier: string;

	reviewSummary: ReviewSummary;
	productName?: string;
	writeAReviewLabel = 'Write A Review';
	writeAReviewType = 'button';
	showNewReviewsQA = false;
	isQAEnabled = false;
	private UNSUBSCRIBE$ = new Subject<void>();
	reviewsMVPRetest = false;
	reviewsProsCons = false;
	isNewReviewsEnabled = false;
	reviewsHighlightsUpdateEnabled = false;
	isReviewsAISummaryEnabled = this.store.selectSignal(
		selectKillswitch('isReviewsAISummaryEnabled')
	);
	isReviewImgPlacementEnabled = this.store.selectSignal(
		selectKillswitch('isReviewImgPlacementEnabled')
	);
	isHighlyRatedReviewsEnabled = this.store.selectSignal(
		selectKillswitch('isHighlyRatedReviewsEnabled')
	);
	isUtagBazaarvoiceEnabled = this.store.selectSignal(
		selectKillswitch('isUtagBazaarvoiceEnabled')
	);
	reviewsObserver: MutationObserver;
	private subscription: Subscription;
	mediaSource = MediaSourceEnum.CAROUSEL;

	ngOnInit(): void {
		this.handleKillSwitch();
		if (isPlatformBrowser(this.platformId)) {
			this.setBVPixelObserver();

			this.eventBusService.on(Events.QAndAEnabled, (isEnabled) => {
				this.isQAEnabled = isEnabled;
				if (isEnabled) {
					this.handleQAndAEnabled().subscribe(() => this.fetchReviews());
				} else {
					this.fetchReviews();
				}
			});

			if (this.isReviewImgPlacementEnabled()) {
				this.reviewService
					.getReviewsMultimedia$(this.productIdentifier)
					.subscribe((reviewsMultimedia) => {
						this.store.dispatch(addProductReviewsMultimedia({ reviewsMultimedia }));
					});
			}
		} else {
			//[INFO] Run in SSR to load the review information.
			this.fetchReviews();
		}

		this.store
			.select(selectProductReviewSummary)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((summary: ReviewSummary) => {
				this.reviewSummary = summary;
			});
		this.store
			.select(selectProductIncludedInformation)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((product: ProductReviewed) => {
				this.productName = product.name;
			});
	}

	ngOnDestroy() {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	handleQAndAEnabled(): Observable<KillSwitches> {
		return this.killSwitchService.getKillSwitchesEnvironment$().pipe(
			tap((data) => {
				this.checkKillSwitch('showNewReviewsQA', data);
			})
		);
	}

	fetchReviews() {
		const requestObservables: Observable<ReviewReturn | Review[] | Statistics>[] = this
			.isNewReviewsEnabled
			? [
					this.reviewService.getReviews$(this.productIdentifier, {
						sortBy: 'SubmissionTime:desc',
						limitTo: 5
					})
				]
			: [
					this.reviewService.getReviews$(this.productIdentifier),
					this.reviewService.getReviewsMultimedia$(this.productIdentifier)
				];

		if (this.isQAEnabled && this.showNewReviewsQA !== undefined) {
			requestObservables.push(this.reviewService.getStatistics$(this.productIdentifier));
		}
		forkJoin(requestObservables)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((results: [ReviewReturn, Review[], Statistics]) => {
				const [reviews] = results;
				let statistics,
					reviewsMultimedia = undefined;
				if (this.isNewReviewsEnabled) {
					statistics = { ...results.slice(1)[0] };
				} else {
					[reviewsMultimedia, statistics] = results.slice(1);
				}
				this.store.dispatch(
					setProductReviewState({
						productIdentifier: this.productIdentifier,
						reviewSummary: reviews.reviewSummary || this.reviewSummary,
						reviews: reviews.reviews,
						product: reviews.product
					})
				);
				if (!this.isNewReviewsEnabled && !!reviewsMultimedia) {
					this.store.dispatch(addProductReviewsMultimedia({ reviewsMultimedia }));
				}

				if (this.isQAEnabled && this.showNewReviewsQA !== undefined && !!statistics) {
					this.store.dispatch(setProductStatistics({ statistics }));
				}
			});
	}

	private handleKillSwitch(): void {
		this.killSwitchService
			.getKillSwitchesEnvironment$()
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				map((data) => [
					this.checkKillSwitch('reviewsMVPRetest', data),
					this.checkKillSwitch('reviewsProsCons', data),
					this.checkKillSwitch('isNewReviewsEnabled', data),
					this.checkKillSwitch('reviewsHighlightsUpdateEnabled', data)
				])
			)
			.subscribe(
				([
					reviewsMVPRetest,
					reviewsProsCons,
					isNewReviewsEnabled,
					reviewsHighlightsUpdateEnabled
				]) => {
					this.reviewsMVPRetest = reviewsMVPRetest;
					this.reviewsProsCons = reviewsProsCons;
					this.isNewReviewsEnabled = isNewReviewsEnabled;
					this.reviewsHighlightsUpdateEnabled = reviewsHighlightsUpdateEnabled;
				}
			);
	}

	private checkKillSwitch(name: string, data: KillSwitches): boolean {
		return this.utilityService.checkKillSwitch(name, data, chain.chainIdentifierAbbr);
	}

	setBVPixelObserver(): void {
		this.subscription = this.observeDomChanges().subscribe((mutation) => {
			this.addListenertohandleBVPixel(mutation);
		});
	}

	private observeDomChanges(): Observable<MutationRecord[]> {
		return new Observable((observer) => {
			const callback = (mutationsList: MutationRecord[]) => {
				observer.next(mutationsList);
			};

			const observerInstance = new MutationObserver(callback);
			const config = { childList: true, subtree: true };

			observerInstance.observe(this.document.body, config);

			this.reviewsObserver = observerInstance;
			// Cleanup function
			return () => observerInstance.disconnect();
		});
	}

	handleBVPixel(): void {
		if (isPlatformBrowser(this.platformId)) {
			this.store
				.select(selectProductReviewSummary)
				.pipe(take(1))
				.subscribe((review: ReviewSummary) => {
					const bvInViewData: BVInViewData = {
						brand: review.brand,
						bvProduct: BVProduct.RATINGSANDREVIEWS,
						productId: this.productIdentifier
					};
					const bvObject: BVEventObject = {
						...bvInViewData,
						type: BVEventType.PRODUCT,
						categoryId: review.category,
						numReviews: review.reviewCount,
						avgRating: review.averageRating,
						percentRecommended: review.recommendationPercentage
					};

					BV.pixel.trackPageView(bvObject);

					const element = this.renderer.selectRootElement(this.el.nativeElement, true);
					if (element) {
						this.intersectionService.initIntersectionObserver(element, () => {
							this.handleBVInViewEvents(bvInViewData);
							this.intersectionService.disconnectObserver();
						});
					}
				});
		}
	}

	handleBVInViewEvents(bvInViewData: BVInViewData): void {
		const trackViewUCGData: TrackViewUCGObject = {
			minPixels: 100,
			containerId: 'product-reviews-highlight-container'
		};
		BV.pixel.trackInView(bvInViewData, trackViewUCGData);
		BV.pixel.trackViewedCGC(bvInViewData, {
			...trackViewUCGData,
			minTime: 5000
		});
	}

	addListenertohandleBVPixel(mutations: MutationRecord[]): void {
		if (
			isPlatformBrowser(this.platformId) &&
			mutations.length > 0 &&
			!!window['BV'] &&
			BV?.pixel
		) {
			this.store.dispatch(setBVPixelIsAvailable({ isBVPixelReady: true }));
			this.handleBVPixel();
			this.reviewsObserver.disconnect();
		}
	}
}
